import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import { IPackagesItem } from '../../../../../../modules/API/Defaults/models/packages'
import { IQualityItem } from '../../../../../../modules/API/Defaults/models/quality'
import { ISizeItem } from '../../../../../../modules/API/Products/models/size'
import { ISortingLines } from '../../../../../../modules/API/Miun/models/sortingList'
import { ISortingLinePostData } from '../../../../../../modules/API/Miun/models/fetchData'
import selectOnFocus from '../../../../../../modules/autoselect'
import PackageSelector from '../../../../../../generic/ui/Selectors/PackageSelector'
import SizeSelector from '../../../../../../generic/ui/Selectors/SizeSelector'
import QualitySelector from '../../../../../../generic/ui/Selectors/QualitySelector'
import IconTextField from '../../../../../../generic/ui/Input/IconTextField'
import { AccentButton } from '../../../../../../generic/ui/Button/AccentButton'
import {
    FormShell,
    FormGrid,
    FormField,
    FormFooter,
} from '../../../../../../generic/ui/FormLayout'
import HashIcon from '../../../../../../generic/icons/IconLib/HashIcon'
import ScaleIcon from '../../../../../../generic/icons/IconLib/ScaleIcon'

type Props = {
    packageList: IPackagesItem[]
    qualityList: IQualityItem[]
    sizeList: ISizeItem[]
    selectedLine: ISortingLines | null
    saveLine: (data: ISortingLinePostData) => void
}

const MiunLineModal: React.FC<Props> = ({
    packageList,
    qualityList,
    sizeList,
    selectedLine,
    saveLine,
}) => {
    const [packageID, setPackageID] = useState<number>(selectedLine?.PackageID || 0)
    const [sizeID, setSizeID] = useState<number>(selectedLine?.SizeID || 0)
    const [qualityID, setQualityID] = useState<number>(selectedLine?.QualityID || 0)
    const [qty, setQty] = useState<number>(selectedLine?.Qty || 0)
    const [weightBruto, setWeightBruto] = useState<number>(selectedLine?.WeightBruto || 0)
    const [remarks, setRemarks] = useState<string>(selectedLine?.Remarks || '')

    const packageObj = useMemo<IPackagesItem | null>(
        () => packageList.find((p) => p.PackID === packageID) ?? null,
        [packageList, packageID],
    )
    const sizeObj = useMemo<ISizeItem | null>(
        () => sizeList.find((s) => s.Code === sizeID) ?? null,
        [sizeList, sizeID],
    )
    const qualityObj = useMemo<IQualityItem | null>(
        () => qualityList.find((q) => q.QualityID === qualityID) ?? null,
        [qualityList, qualityID],
    )

    const isActiveSave = !!packageID && !!sizeID && !!qualityID && qty > 0

    const handleSubmit = () => {
        if (!isActiveSave) return
        const data: ISortingLinePostData = {
            PackageID: packageID,
            SizeID: sizeID,
            QualityID: qualityID,
            Qty: qty,
            WeightBruto: weightBruto,
            Remarks: remarks,
        }
        saveLine(data)
    }

    return (
        <FormShell>
            <FormGrid>
                <FormField label='אריזה' required={!packageID} fullWidth>
                    <PackageSelector
                        packages={packageList}
                        value={packageObj}
                        onSelect={(p) => setPackageID(p?.PackID ?? 0)}
                    />
                </FormField>

                <FormField label='גודל' required={!sizeID}>
                    <SizeSelector
                        sizes={sizeList}
                        value={sizeObj}
                        onSelect={(s) => setSizeID(s?.Code ?? 0)}
                    />
                </FormField>

                <FormField label='איכות' required={!qualityID}>
                    <QualitySelector
                        qualities={qualityList}
                        value={qualityObj}
                        onSelect={(q) => setQualityID(q?.QualityID ?? 0)}
                    />
                </FormField>

                <FormField label='כמות' required={!qty}>
                    <IconTextField
                        type='number'
                        value={qty || ''}
                        onChange={(e) => setQty(Number(e.target.value))}
                        onClick={selectOnFocus}
                        leadingIcon={<HashIcon />}
                        placeholder='כמות'
                    />
                </FormField>

                <FormField label='משקל ברוטו'>
                    <IconTextField
                        type='number'
                        value={weightBruto || ''}
                        onChange={(e) => setWeightBruto(Number(e.target.value))}
                        onClick={selectOnFocus}
                        leadingIcon={<ScaleIcon />}
                        placeholder='משקל'
                    />
                </FormField>

                <FormField label='הערה' fullWidth>
                    <RemarkArea
                        value={remarks}
                        onChange={(e) => setRemarks(e.target.value)}
                        placeholder='הערה'
                        rows={2}
                    />
                </FormField>
            </FormGrid>

            <FormFooter>
                <AccentButton
                    type='button'
                    onClick={handleSubmit}
                    disabled={!isActiveSave}
                    fullWidth={false}
                >
                    שמור
                </AccentButton>
            </FormFooter>
        </FormShell>
    )
}

export default MiunLineModal

const RemarkArea = styled.textarea`
    width: 100%;
    min-height: 4.5rem;
    background: ${({ theme }) => theme.colors.surface};
    border: 1px solid ${({ theme }) => theme.colors.inputBorder};
    border-radius: 0.875rem;
    padding: 0.625rem 0.875rem;
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: ${({ theme }) => theme.text.lg};
    color: ${({ theme }) => theme.colors.ink};
    outline: none;
    resize: vertical;
    direction: rtl;
    transition:
        border-color ${({ theme }) => theme.motion.fast} ease,
        box-shadow ${({ theme }) => theme.motion.fast} ease;

    &::placeholder {
        color: ${({ theme }) => theme.colors.ink4};
    }

    &:hover:not(:disabled):not(:focus) {
        border-color: ${({ theme }) => theme.colors.inputBorderHover};
    }

    &:focus {
        border-color: ${({ theme }) => theme.colors.ink};
        box-shadow: ${({ theme }) => theme.shadows.focus};
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.lg};
    }
`
